"use client";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { FileSpreadsheet, FileText } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { exportToExcel, exportToPDF } from "@/lib/excelExport";

interface StaffPerformanceDashboardProps {
  data: {
    dateRange: {
      start: string;
      end: string;
    };
    performance: Array<{
      staffId: string;
      staffName: string;
      role: string;
      completedJobs: number;
      cancelledJobs: number;
      totalJobs: number;
      completionRate: number;
      cancellationRate: number;
      revenue: number;
      status: "high" | "average" | "needs-attention";
      isLowOutput: boolean;
    }>;
    summary: {
      highPerformers: number;
      needsAttention: number;
      averagePerformers: number;
    };
  };
}

export function StaffPerformanceDashboard({
  data,
}: StaffPerformanceDashboardProps) {
  // Keep all validly named rows so active staff without jobs still appear in reports.
  const filteredPerformance = data.performance.filter(
    (member) => !!member.staffName?.trim(),
  );

  // Recalculate summary based on filtered data
  const filteredSummary = {
    highPerformers: filteredPerformance.filter((m) => m.status === "high")
      .length,
    needsAttention: filteredPerformance.filter(
      (m) => m.status === "needs-attention",
    ).length,
    averagePerformers: filteredPerformance.filter((m) => m.status === "average")
      .length,
  };

  const getExportData = () => {
    return filteredPerformance.map((member) => ({
      "Staff Name": member.staffName,
      Role: member.role,
      "Jobs Completed": member.completedJobs,
      "Jobs Cancelled": member.cancelledJobs,
      "Total Jobs": member.totalJobs,
      "Completion Rate (%)": member.completionRate,
      "Cancellation Rate (%)": member.cancellationRate,
      Revenue: member.revenue,
      Status: member.status,
    }));
  };

  const handleExportExcel = () => {
    exportToExcel(
      getExportData(),
      "Staff_Performance_Report",
      "Staff Performance",
    );
  };

  const handleExportPDF = () => {
    exportToPDF(getExportData(), "Staff_Performance_Report", {
      title: "Staff Performance Report",
      subtitle: "Performance leaderboard by staff",
      dateRange: data.dateRange,
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "high":
        return (
          <Badge className="bg-green-500 hover:bg-green-600">
            High Performer
          </Badge>
        );
      case "needs attention":
      case "needs-attention":
        return <Badge variant="destructive">Needs Attention</Badge>;
      default:
        return <Badge variant="secondary">Average</Badge>;
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Staff Performance Leaderboard</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleExportExcel}>
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Excel
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportPDF}>
              <FileText className="h-4 w-4 mr-2" />
              PDF
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Staff Name</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead className="text-right">Jobs Completed</TableHead>
                  <TableHead className="text-right">Jobs Cancelled</TableHead>
                  <TableHead className="text-right">Completion Rate</TableHead>
                  <TableHead className="text-right">
                    Revenue Generated
                  </TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPerformance.length > 0 ? (
                  filteredPerformance.map((member) => (
                    <TableRow key={member.staffId}>
                      <TableCell className="font-medium">
                        {member.staffName}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{member.role}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {member.completedJobs}
                      </TableCell>
                      <TableCell className="text-right text-red-600">
                        {member.cancelledJobs}
                      </TableCell>
                      <TableCell className="text-right">
                        <Badge
                          variant={
                            member.completionRate >= 80
                              ? "default"
                              : member.completionRate >= 60
                                ? "secondary"
                                : "destructive"
                          }
                        >
                          {member.completionRate}%
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(member.revenue)}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {getStatusBadge(member.status)}
                          {member.isLowOutput && (
                            <Badge variant="outline" className="text-xs">
                              Low Output
                            </Badge>
                          )}
                          {member.cancellationRate > 20 && (
                            <Badge
                              variant="outline"
                              className="text-xs text-red-600"
                            >
                              High Cancellations
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-muted-foreground">
                      No staff performance data available for the selected date range.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Performance Summary */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              High Performers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {filteredSummary.highPerformers}
            </div>
            <p className="text-xs text-muted-foreground">
              &ge;95% completion, &le;5% cancellation
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              Needs Attention
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {filteredSummary.needsAttention}
            </div>
            <p className="text-xs text-muted-foreground">
              Staff requiring support
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">
              Average Performers
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {filteredSummary.averagePerformers}
            </div>
            <p className="text-xs text-muted-foreground">
              Staff performing at average level
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
